import { api } from './client';
import type {
  FillLobbyBotsRequest,
  HealthResponse,
  InvitePlayersResponse,
  LaunchGameRequest,
  LobbyActionResponse,
  LobbyBotSlotRequest,
  LobbyChatMessagesResponse,
  LobbyKickRequest,
  LobbyMessagesResponse,
  LobbyRequest,
  LobbyResponse,
  LobbySeatBotRequest,
  LobbySettingsPatch,
  LobbyStatusResponse,
  LobbySummary,
  LobbyWatchResponse,
  MutationResponse,
  PoolStatusResponse,
  ReconnectResponse,
  SeriesCreateRequest,
  SeriesCreateResponse,
} from './types';

const V1 = '/api/v1';

export const health = {
  get: () => api.get<HealthResponse>('/health'),
};

export const lobbies = {
  list: () => api.get<LobbyStatusResponse>(`${V1}/lobby/all/status`),
  create: (payload: LobbyRequest) => api.post<LobbyResponse>(`${V1}/lobby/create`, payload),
  createAutoMatch: (payload: LobbyRequest) =>
    api.post<LobbyResponse>(`${V1}/lobby/createAutoMatch`, payload),
  createSeries: (payload: SeriesCreateRequest) =>
    api.post<SeriesCreateResponse>(`${V1}/lobby/series/create`, payload),
  launch: (payload: LaunchGameRequest) => api.post<MutationResponse>(`${V1}/lobby/launch`, payload),
  fillBots: (payload: FillLobbyBotsRequest) =>
    api.post<MutationResponse>(`${V1}/lobby/fill-bots`, payload),
  summary: (lobbyId: string | number) => api.get<LobbySummary>(`${V1}/lobby/${lobbyId}/lobby`),
  // The shape has changed over time (bare array, {history}, {items}, {item}),
  // so the caller hands the payload to normalizeHistory rather than trusting a type.
  history: (params: { match_id?: number; bot_id?: string; last?: number; limit?: number } = {}) =>
    api.get<unknown>(`${V1}/lobby/history`, { query: params }),
};

export const pool = {
  status: () => api.get<PoolStatusResponse>(`${V1}/pool/status`),
  reconnect: (forceAll = false) =>
    api.post<ReconnectResponse>(`${V1}/pool/reconnect`, { force_all: forceAll }),
  logout: () => api.post<MutationResponse>(`${V1}/pool/logout`),
};

export const bot = {
  info: (botId: string) => api.get<unknown>(`${V1}/pool/bot/${botId}`),
  lobby: (botId: string) => api.get<{ lobby_info?: { lobby_id?: number } }>(`${V1}/pool/bot/${botId}/lobby`),
  invite: (botId: string, steamIds: string[]) =>
    api.post<InvitePlayersResponse>(`${V1}/pool/bot/${botId}/invite`, { steam_ids: steamIds }),
  leaveLobby: (botId: string) => api.post<MutationResponse>(`${V1}/pool/bot/${botId}/leave`),
  abandonLobby: (botId: string) => api.post<MutationResponse>(`${V1}/pool/bot/${botId}/abandon`),
  logout: (botId: string) => api.post<MutationResponse>(`${V1}/pool/bot/${botId}/logout`),
  destroyLobby: (botId: string) => api.post<MutationResponse>(`${V1}/pool/bot/${botId}/lobby/destroy`),
  sendMessage: (botId: string, message: string) =>
    api.post<MutationResponse>(`${V1}/pool/bot/${botId}/lobby/message`, { message }),
  sentMessages: (botId: string) =>
    api.get<LobbyMessagesResponse>(`${V1}/pool/bot/${botId}/lobby/messages`),
  chatMessages: (botId: string) =>
    api.get<LobbyChatMessagesResponse>(`${V1}/pool/bot/${botId}/lobby/chat/messages`),
  /**
   * Long poll: resolves as soon as the lobby differs from `version`, or after
   * `timeoutSeconds` with the same version. Omit the version on the first call.
   */
  watchLobby: (
    botId: string,
    version: string | undefined,
    timeoutSeconds: number,
    signal?: AbortSignal,
  ) =>
    api.get<LobbyWatchResponse>(`${V1}/pool/bot/${botId}/lobby/watch`, {
      query: { version, timeout_seconds: timeoutSeconds },
      signal,
    }),
  changeGameMode: (botId: string, gameMode: number) =>
    api.post<MutationResponse>(`${V1}/pool/bot/${botId}/lobby/game-mode`, { game_mode: gameMode }),
  changeSettings: (botId: string, settings: LobbySettingsPatch) =>
    api.post<MutationResponse>(`${V1}/pool/bot/${botId}/lobby/change-settings`, settings),
  setSelectionPriority: (botId: string, priority: 'automatic' | 'manual') =>
    api.post<MutationResponse>(`${V1}/pool/bot/${botId}/selection-priority`, { priority }),
  flipTeams: (botId: string) =>
    api.post<LobbyActionResponse>(`${V1}/pool/bot/${botId}/lobby/flip-teams`),
  shuffleTeams: (botId: string) =>
    api.post<LobbyActionResponse>(`${V1}/pool/bot/${botId}/lobby/shuffle`),
  kick: (botId: string, payload: LobbyKickRequest) =>
    api.post<LobbyActionResponse>(`${V1}/pool/bot/${botId}/lobby/kick`, payload),
  seatBot: (botId: string, payload: LobbySeatBotRequest) =>
    api.post<LobbyActionResponse>(`${V1}/pool/bot/${botId}/lobby/seat-bot`, payload),
  setBotSlot: (botId: string, payload: LobbyBotSlotRequest) =>
    api.post<LobbyActionResponse>(`${V1}/pool/bot/${botId}/lobby/bot-slot`, payload),
};
